import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import CustomAnalysisPageManager from '../components/admin/CustomAnalysisPageManager';
import './AdminPage.css';

const AdminPage = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [csvData, setCsvData] = useState([]);
  const [dataType, setDataType] = useState('legacy');
  const [currentHash, setCurrentHash] = useState('');
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [editingCell, setEditingCell] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [notification, setNotification] = useState(null);
  const [currentSubPage, setCurrentSubPage] = useState('data-management');
  const [selectedHash, setSelectedHash] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [selectedSibling, setSelectedSibling] = useState(null);
  const [currentSiblings, setCurrentSiblings] = useState([]);
  const [showSiblingPopup, setShowSiblingPopup] = useState(false);
  const [siblingPopupNode, setSiblingPopupNode] = useState(null);
  const [isAdvancedMode, setIsAdvancedMode] = useState(false); // 기본값을 false로 설정 (History Manual Control 비활성화)
  const [tooltip, setTooltip] = useState({ show: false, text: '', x: 0, y: 0 });
  // 원본 행 스냅샷(legacy diff 판단용): key는 id
  const [originalRowsById, setOriginalRowsById] = useState({});

  // 사용자 관리 상태
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [syncingUsers, setSyncingUsers] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [showUserEditModal, setShowUserEditModal] = useState(false);
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [userSortField, setUserSortField] = useState('created_at');
  const [userSortDirection, setUserSortDirection] = useState('desc');
  
  // 사용자 생성 모달 상태
  const [showCreateUserModal, setShowCreateUserModal] = useState(false);
  const [newUser, setNewUser] = useState({
    email: '',
    name: '',
    available_data_types: []
  });

  // 보안 모니터링 상태
  const [securityData, setSecurityData] = useState(null);
  const [loadingSecurity, setLoadingSecurity] = useState(false);
  const [securityRefreshInterval, setSecurityRefreshInterval] = useState(null);

  // 자산 설정 상태
  const [assetConfigs, setAssetConfigs] = useState([]);
  const [loadingAssetConfigs, setLoadingAssetConfigs] = useState(false);
  const [updatingAssetOrder, setUpdatingAssetOrder] = useState(false);
  const [newAssetSymbol, setNewAssetSymbol] = useState('');
  const [legacyFlowEmail, setLegacyFlowEmail] = useState('');
  const [legacyFlowEvents, setLegacyFlowEvents] = useState([]);
  const [loadingLegacyFlow, setLoadingLegacyFlow] = useState(false);
  const [legacyFlowLimit, setLegacyFlowLimit] = useState(300);
  const [fixedIncomeFlowEmail, setFixedIncomeFlowEmail] = useState('');
  const [fixedIncomeFlowEvents, setFixedIncomeFlowEvents] = useState([]);
  const [loadingFixedIncomeFlow, setLoadingFixedIncomeFlow] = useState(false);
  const [fixedIncomeFlowLimit, setFixedIncomeFlowLimit] = useState(300);
  const [vePriceRows, setVePriceRows] = useState([]);
  const [loadingVePrices, setLoadingVePrices] = useState(false);
  const [savingVePriceMonth, setSavingVePriceMonth] = useState(null);

  // 서브 페이지 정의
  const subPages = [
    { id: 'data-management', title: '데이터 관리', icon: '📊' },
    { id: 'custom-analysis-pages', title: '커스텀 분석 페이지', icon: '🧩' },
    { id: 've-token-prices', title: 'VE 토큰 가격', icon: '🪙' },
    { id: 'legacy-flow-history', title: 'Legacy 입출 히스토리', icon: '🧾' },
    { id: 'fixed-income-deposit-history', title: 'Fixed Income 입출 히스토리', icon: '🏦' },
    { id: 'user-management', title: '사용자 관리', icon: '👥' },
    { id: 'asset-config', title: '자산 설정', icon: '💰' }
  ];

  const showNotification = (message, type = 'info') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 4000); // 4초로 연장
  };

  // 여러 줄 메시지를 지원하는 알림 함수
  const showMultiLineNotification = (messages, type = 'info') => {
    const combinedMessage = Array.isArray(messages) ? messages.join('\n') : messages;
    setNotification({ message: combinedMessage, type });
    setTimeout(() => setNotification(null), 4000);
  };

  // 툴팁 표시 함수
  const showTooltip = (text, event) => {
    setTooltip({
      show: true,
      text,
      x: event.clientX,
      y: event.clientY - 30
    });
  };

  // 툴팁 숨김 함수
  const hideTooltip = () => {
    setTooltip({ show: false, text: '', x: 0, y: 0 });
  };

  // History Manual Control 토글 함수
  const toggleHistoryManualControl = async () => {
    const newMode = !isAdvancedMode;
    setIsAdvancedMode(newMode);
    
    // History Manual Control을 비활성화할 때 현재 체크아웃 포인트가 아닌 경우 자동 이동
    if (!newMode && selectedHash && selectedHash !== currentHash) {
      try {
        setSelectedHash(null);
        setPreviewData(null);
        await loadCsvData(); // 현재 체크아웃 포인트 데이터 로드
        showNotification('History Manual Control이 비활성화되어 현재 체크아웃 포인트로 이동했습니다.', 'info');
      } catch (error) {
        console.error('Error moving to current checkout:', error);
        showNotification('현재 체크아웃 포인트로 이동하는데 실패했습니다.', 'error');
      }
    }
  };

  // 데이터 검증 함수들
  const validateEmail = (email) => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
  };

  const validateNumericField = (value) => {
    if (value === '' || value === '0') return true;
    
    // 쉼표 제거
    const cleanValue = value.replace(/,/g, '');
    
    // 숫자 형식 검증 (정수, 소수점, 과학적 표기법)
    const numericRegex = /^[0-9]+(\.[0-9]+)?([eE][+-]?[0-9]+)?$/;
    if (!numericRegex.test(cleanValue)) return false;
    
    // 음수 검증
    const numValue = parseFloat(cleanValue);
    return numValue >= 0;
  };

  const validateDateField = (value) => {
    if (value === '') return true;
    
    // yyyy-mm-dd 형식 검증
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (!dateRegex.test(value)) return false;
    
    // 유효한 날짜인지 검증
    const date = new Date(value);
    return !isNaN(date.getTime());
  };

  const validateField = (column, value) => {
    // 이메일 필드 검증 (모든 데이터 타입에서 필수)
    if (column.toLowerCase().includes('email')) {
      if (!value || value.trim() === '') {
        return false; // 빈 값은 허용하지 않음
      }
      return validateEmail(value);
    }
    
    // 잔액/수량 필드 검증 (legacy)
    if (dataType === 'legacy') {
      const balanceFields = [
        'ETH Balance', 'BTC Balance', 'USDT Balance', 'USDC Balance', 'KRW Balance',
        'VEEE Quantity', 'VEBE Quantity'
      ];
      
      if (balanceFields.some(field => column.includes(field))) {
        return validateNumericField(value);
      }
    }
    
    // 날짜 필드 검증 (preferred-return, fixed-income)
    if (dataType === 'preferred-return' || dataType === 'fixed-income') {
      if (column.toLowerCase().includes('date')) {
        return validateDateField(value);
      }
      
      // partial_withdraw 필드 검증 (숫자 필드)
      if (column === 'partial_withdraw') {
        return validateNumericField(value);
      }
    }
    
    return true; // 다른 필드들은 검증하지 않음
  };

  const getFieldValidationMessage = (column, value) => {
    if (column.toLowerCase().includes('email')) {
      if (!value || value.trim() === '') {
        return '이메일은 필수 입력 항목입니다.';
      }
      return '올바른 이메일 형식을 입력해주세요. (예: user@example.com)';
    }
    
    if (dataType === 'legacy') {
      const balanceFields = [
        'ETH Balance', 'BTC Balance', 'USDT Balance', 'USDC Balance', 'KRW Balance',
        'VEEE Quantity', 'VEBE Quantity'
      ];
      
      if (balanceFields.some(field => column.includes(field))) {
        return '숫자만 입력해주세요. (쉼표 제외, 0 이상, 과학적 표기법 허용: 1.136e-05)';
      }
    }
    
    if (dataType === 'preferred-return' || dataType === 'fixed-income') {
      if (column.toLowerCase().includes('date')) {
        return '날짜 형식을 yyyy-mm-dd로 입력해주세요. (예: 2024-12-31)';
      }
      
      if (column === 'partial_withdraw') {
        return '숫자만 입력해주세요. (쉼표 제외, 0 이상, 과학적 표기법 허용: 1.136e-05)';
      }
    }
    
    return '올바른 형식을 입력해주세요.';
  };

  const getPlaceholder = (column) => {
    if (column.toLowerCase().includes('email')) {
      return 'user@example.com';
    }
    
    if (dataType === 'legacy') {
      const balanceFields = [
        'ETH Balance', 'BTC Balance', 'USDT Balance', 'USDC Balance', 'KRW Balance',
        'VEEE Quantity', 'VEBE Quantity'
      ];
      
      if (balanceFields.some(field => column.includes(field))) {
        return '0.00 또는 1.136e-05';
      }
    }
    
    if (dataType === 'preferred-return' || dataType === 'fixed-income') {
      if (column.toLowerCase().includes('date')) {
        return '2024-12-31';
      }
      
      if (column === 'partial_withdraw') {
        return '0.0 또는 1.136e-05';
      }
    }
    
    return '';
  };

  const loadCsvData = useCallback(async (hashId = null) => {
    try {
      const token = localStorage.getItem('authToken');
      
      // URL 파라미터에서 데이터 타입 확인 (우선순위)
      const urlDataType = searchParams.get('dataType');
      const effectiveDataType = urlDataType || dataType;

      // 신규 export API로 대체 (CSV 전체를 반환)
      const response = await axios.get(`/api/external/export/${effectiveDataType}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const csvText = response.data;
      const lines = csvText.split('\n').filter(line => line.trim());

      if (lines.length > 0) {
        const headerRow = lines[0].split(',').map(header => header.trim().replace(/"/g, ''));
        const dataRows = lines.slice(1).map((line, index) => {
          const values = [];
          let current = '';
          let inQuotes = false;
          for (let i = 0; i < line.length; i++) {
            const char = line[i];
            if (char === '"') {
              inQuotes = !inQuotes;
            } else if (char === ',' && !inQuotes) {
              values.push(current.trim());
              current = '';
            } else {
              current += char;
            }
          }
          values.push(current.trim());
          const row = {};
          headerRow.forEach((header, i) => {
            row[header] = values[i] || '';
          });
          row._id = index;
          return row;
        });

        // export는 해시 미리보기 개념이 없으므로 항상 현재 데이터를 세팅
        setPreviewData(null);
        setCsvData(dataRows);
        // 원본 스냅샷 저장(기존 행만: id 존재 시)
        const orig = {};
        dataRows.forEach(r => {
          if (r && r.id) {
            orig[r.id] = { ...r };
          }
        });
        setOriginalRowsById(orig);
      }
    } catch (error) {
      console.error('Error loading CSV data:', error);
      showNotification('데이터를 불러오는데 실패했습니다.', 'error');
    }
  }, [dataType, searchParams]);

  const loadCurrentHash = useCallback(async () => {
    try {
      const token = localStorage.getItem('authToken');
      
      // URL 파라미터에서 데이터 타입 확인 (우선순위)
      const urlDataType = searchParams.get('dataType');
      const effectiveDataType = urlDataType || dataType;
      
      const response = await axios.get(`/api/external/csv-data/${effectiveDataType}/current`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCurrentHash(response.data.current_hash);
    } catch (error) {
      console.error('Error loading current hash:', error);
    }
  }, [dataType, searchParams]);

  // 데이터 타입 변경 시 상태 초기화
  const handleDataTypeChange = (newDataType) => {
    setDataType(newDataType);
    setSelectedHash(null);
    setPreviewData(null);
    setShowHistory(false);
  };

  const loadHistory = async () => {
    // 편집 모드가 활성화되어 있으면 강제로 종료
    if (editingCell) {
      setEditingCell(null);
      setEditValue('');
    }
    
    try {
      const token = localStorage.getItem('authToken');
      
      // URL 파라미터에서 데이터 타입 확인 (우선순위)
      const urlDataType = searchParams.get('dataType');
      const effectiveDataType = urlDataType || dataType;
      
      const response = await axios.get(`/api/external/csv-data/${effectiveDataType}/history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // 히스토리 데이터를 블록 라인 구조로 변환
      const historyData = response.data.history || response.data.chain || [];
      
      // 데이터가 너무 많으면 처리하지 않음
      if (historyData.length > 100) {
        showNotification('히스토리가 너무 많습니다. 최근 100개만 표시됩니다.', 'warning');
        historyData.splice(100);
      }
      
      // 현재 선택된 해시나 미리보기 중인 해시를 우선적으로 고려
      const targetHash = selectedHash || currentHash;
      
      // 전체 히스토리에서 형제 관계를 먼저 계산
      const fullHistoryWithSiblings = buildHistoryBlockLines(historyData);
      
      // 선택된 형제 노드가 있으면 해당 노드를 기준으로 히스토리 구성
      if (selectedSibling) {
        const siblingNode = historyData.find(item => item.hash === selectedSibling);
        if (siblingNode) {
          // 선택된 노드의 부모들을 찾아서 경로 구성
          const buildPathToSibling = (targetHash) => {
            const path = [];
            let current = historyData.find(item => item.hash === targetHash);
            
            while (current) {
              path.unshift(current);
              current = historyData.find(item => item.hash === current.previous_hash);
            }
            
            return path;
          };
          
          // 선택된 노드까지의 경로와 그 자식들을 포함하는 새로운 히스토리 구성
          const pathToSibling = buildPathToSibling(selectedSibling);
          const newHistoryData = [...pathToSibling];
          
          // 선택된 노드의 자식들을 재귀적으로 찾아서 추가
          const findChildren = (parentHash) => {
            const children = historyData.filter(item => item.previous_hash === parentHash);
            children.forEach(child => {
              if (!newHistoryData.find(item => item.hash === child.hash)) {
                newHistoryData.push(child);
                findChildren(child.hash);
              }
            });
          };
          
          findChildren(selectedSibling);
          
          // 전체 히스토리에서 형제 관계 정보를 가져와서 새로운 히스토리에 적용
          const blockLines = buildHistoryBlockLinesWithSiblings(newHistoryData, historyData);
          setHistory(blockLines);
        } else {
          // 선택된 형제 노드를 찾을 수 없으면 전체 히스토리 표시
          setHistory(fullHistoryWithSiblings);
        }
      } else if (targetHash) {
        // 선택된 해시나 현재 해시가 있으면 해당 해시 체인을 보여줌
        const targetNode = historyData.find(item => item.hash === targetHash);
        if (targetNode) {
          // 대상 노드의 부모들을 찾아서 경로 구성
          const buildPathToTarget = (targetHash) => {
            const path = [];
            let current = historyData.find(item => item.hash === targetHash);
            
            while (current) {
              path.unshift(current);
              current = historyData.find(item => item.hash === current.previous_hash);
            }
            
            return path;
          };
          
          // 대상 노드까지의 경로와 그 자식들을 포함하는 새로운 히스토리 구성
          const pathToTarget = buildPathToTarget(targetHash);
          const newHistoryData = [...pathToTarget];
          
          // 대상 노드의 자식들을 재귀적으로 찾아서 추가
          const findChildren = (parentHash) => {
            const children = historyData.filter(item => item.previous_hash === parentHash);
            children.forEach(child => {
              if (!newHistoryData.find(item => item.hash === child.hash)) {
                newHistoryData.push(child);
                findChildren(child.hash);
              }
            });
          };
          
          findChildren(targetHash);
          
          // 전체 히스토리에서 형제 관계 정보를 가져와서 새로운 히스토리에 적용
          const blockLines = buildHistoryBlockLinesWithSiblings(newHistoryData, historyData);
          setHistory(blockLines);
        } else {
          // 대상 노드를 찾을 수 없으면 전체 히스토리 표시
          setHistory(fullHistoryWithSiblings);
        }
      } else {
        // 선택된 해시가 없으면 전체 히스토리 표시
        setHistory(fullHistoryWithSiblings);
      }
      
      setShowHistory(true);
    } catch (error) {
      console.error('Error loading history:', error);
      showNotification('히스토리를 불러오는데 실패했습니다.', 'error');
    }
  };

  // 히스토리 데이터를 블록 라인 구조로 변환하는 함수 (형제 관계 정보 포함)
  const buildHistoryBlockLinesWithSiblings = (historyData, fullHistoryData) => {
    if (!Array.isArray(historyData) || historyData.length === 0) {
      return [];
    }

    // 해시를 키로 하는 맵 생성
    const hashMap = new Map();
    historyData.forEach(item => {
      hashMap.set(item.hash, {
        ...item,
        children: [],
        siblings: [],
        level: 0
      });
    });

    // 부모-자식 관계 설정
    historyData.forEach(item => {
      const node = hashMap.get(item.hash);
      if (item.previous_hash && hashMap.has(item.previous_hash)) {
        const parent = hashMap.get(item.previous_hash);
        parent.children.push(node);
        node.level = parent.level + 1;
      }
    });

    // 전체 히스토리에서 형제 관계 정보를 가져와서 적용
    historyData.forEach(item => {
      const node = hashMap.get(item.hash);
      if (node && item.previous_hash) {
        // 전체 히스토리에서 같은 previous_hash를 가진 다른 노드들을 찾아서 형제로 설정
        const siblings = fullHistoryData.filter(sibling => 
          sibling.hash !== item.hash && 
          sibling.previous_hash === item.previous_hash
        );
        
        siblings.forEach(sibling => {
          const siblingNode = {
            ...sibling,
            children: [],
            siblings: [],
            level: node.level
          };
          if (!node.siblings.find(s => s.hash === sibling.hash)) {
            node.siblings.push(siblingNode);
          }
        });
      }
    });

    // 루트 노드들 찾기
    const rootNodes = historyData
      .filter(item => !item.previous_hash || !hashMap.has(item.previous_hash))
      .map(item => hashMap.get(item.hash));

    // 각 루트에서 시작하는 라인 생성
    const lines = [];
    rootNodes.forEach(root => {
      const line = buildLineFromNode(root, hashMap);
      lines.push(line);
    });

    return lines;
  };

  // 히스토리 데이터를 블록 라인 구조로 변환하는 함수
  const buildHistoryBlockLines = (historyData) => {
    if (!Array.isArray(historyData) || historyData.length === 0) {
      return [];
    }

    // 해시를 키로 하는 맵 생성
    const hashMap = new Map();
    historyData.forEach(item => {
      hashMap.set(item.hash, {
        ...item,
        children: [],
        siblings: [],
        level: 0
      });
    });

    // 부모-자식 관계 설정
    historyData.forEach(item => {
      const node = hashMap.get(item.hash);
      if (item.previous_hash && hashMap.has(item.previous_hash)) {
        const parent = hashMap.get(item.previous_hash);
        parent.children.push(node);
        node.level = parent.level + 1;
      }
    });

    // 형제 관계 설정 - 같은 부모를 가진 노드들을 형제로 설정
    historyData.forEach(item => {
      const node = hashMap.get(item.hash);
      if (node && item.previous_hash) {
        // 같은 previous_hash를 가진 다른 노드들을 찾아서 형제로 설정
        const siblings = historyData.filter(sibling => 
          sibling.hash !== item.hash && 
          sibling.previous_hash === item.previous_hash
        );
        
        siblings.forEach(sibling => {
          const siblingNode = hashMap.get(sibling.hash);
          if (siblingNode && !node.siblings.find(s => s.hash === sibling.hash)) {
            node.siblings.push(siblingNode);
          }
        });
      }
    });

    // 형제 노드 정보 디버깅
    // console.log('Nodes with siblings:');
    // historyData.forEach(item => {
    //   const node = hashMap.get(item.hash);
    //   if (node && node.siblings.length > 0) {
    //     console.log(`Node ${item.hash} has ${node.siblings.length} siblings:`, node.siblings.map(s => s.hash));
    //   }
    // });

    // 루트 노드들 찾기
    const rootNodes = historyData
      .filter(item => !item.previous_hash || !hashMap.has(item.previous_hash))
      .map(item => hashMap.get(item.hash));

    // 각 루트에서 시작하는 라인 생성
    const lines = [];
    rootNodes.forEach(root => {
      const line = buildLineFromNode(root, hashMap);
      lines.push(line);
    });

    return lines;
  };

  // 노드에서 시작하는 라인을 생성하는 함수
  const buildLineFromNode = (node, hashMap) => {
    const line = [node];
    let current = node;
    
    while (current.children.length > 0) {
      // 자식이 하나뿐이면 그대로 진행
      if (current.children.length === 1) {
        current = current.children[0];
        line.push(current);
      } else {
        // 여러 자식이 있으면 첫 번째 자식으로 진행하고 나머지는 별도 라인으로 처리
        current = current.children[0];
        line.push(current);
        
        // 나머지 자식들을 별도 라인으로 생성
        current.children.slice(1).forEach(child => {
          const childLine = buildLineFromNode(child, hashMap);
          line.push({ type: 'branch', children: childLine });
        });
      }
    }
    
    return line;
  };

  // 트리 노드를 렌더링하는 함수 (미니멀)
  const renderHistoryNode = (node, index, depth = 0) => {
    const isCurrent = node.hash === currentHash;
    const isSelected = node.hash === selectedHash;
    const hasChildren = node.children && node.children.length > 0;
    
    return (
      <div key={node.hash} className="history-tree-node">
        <div className="history-node-content">
          <div 
            className={`history-node-line ${isSelected ? 'selected' : ''}`} 
            style={{ marginLeft: `${depth * 30}px` }}
            onClick={() => handleNodeClick(node)}
          >
            {/* 연결선 */}
            {depth > 0 && <div className="history-connector-line"></div>}
            
            {/* 노드 아이콘 */}
            <div className={`history-node-icon ${isCurrent ? 'current' : ''} ${isSelected ? 'selected' : ''}`}>
              {isCurrent ? '●' : '○'}
            </div>
            
            {/* 마우스 오버 시에만 표시되는 상세 정보 */}
            <div className="history-node-tooltip">
              <div className="tooltip-hash">{node.hash}</div>
              <div className="tooltip-timestamp">
                {new Date(node.timestamp).toLocaleString()}
              </div>
              {node.previous_hash && (
                <div className="tooltip-previous">
                  이전: {node.previous_hash}
                </div>
              )}
            </div>
          </div>
          
          {/* 자식 노드들 */}
          {hasChildren && (
            <div className="history-children">
              {node.children.map((child, childIndex) => (
                <div key={child.hash} className="history-child-node">
                  {renderHistoryNode(child, childIndex, depth + 1)}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  const startEditing = (rowIndex, column) => {
    // id 및 생성/업데이트 시각은 편집 불가
    if (column === 'id' || column === 'created_at' || column === 'updated_at') return;
    
    // preferred-return과 fixed-income에서 maturity_date는 편집 불가 (period 기반으로 자동 계산)
    if ((dataType === 'preferred-return' || dataType === 'fixed-income') && column === 'maturity_date') {
      return;
    }
    
    setEditingCell({ rowIndex, column });
    // 미리보기 모드인지 확인하여 올바른 데이터 소스 사용
    const currentData = previewData || csvData;
    setEditValue(currentData[rowIndex][column] || '');
  };

  const finishEditing = () => {
    if (editingCell) {
      // 필드 검증 수행
      if (!validateField(editingCell.column, editValue)) {
        const errorMessage = getFieldValidationMessage(editingCell.column, editValue);
        showNotification(errorMessage, 'error');
        return; // 검증 실패 시 편집 모드 유지
      }
      
      // 미리보기 모드인지 확인하여 올바른 데이터 소스 사용
      const currentData = previewData || csvData;
      const newData = [...currentData];
      
      // 숫자 필드의 경우 쉼표 제거
      let processedValue = editValue;
      if (dataType === 'legacy') {
        const balanceFields = [
          'ETH Balance', 'BTC Balance', 'USDT Balance', 'USDC Balance', 'KRW Balance',
          'VEEE Quantity', 'VEBE Quantity'
        ];
        
        if (balanceFields.some(field => editingCell.column.includes(field))) {
          processedValue = editValue.replace(/,/g, '');
        }
      }
      
      newData[editingCell.rowIndex][editingCell.column] = processedValue;
      
      if (previewData) {
        setPreviewData(newData);
      } else {
        setCsvData(newData);
      }
      
      setEditingCell(null);
      setEditValue('');
    }
  };

  const addRow = () => {
    // 편집 모드가 활성화되어 있으면 강제로 종료
    if (editingCell) {
      setEditingCell(null);
      setEditValue('');
    }
    
    // 미리보기 모드인지 확인하여 올바른 데이터 소스 사용
    const currentData = previewData || csvData;

    let newRow;
    if (dataType === 'preferred-return' || dataType === 'fixed-income') {
      const today = new Date();
      const yyyy = today.getFullYear();
      const mm = String(today.getMonth() + 1).padStart(2, '0');
      const dd = String(today.getDate()).padStart(2, '0');
      const yyyyMmDd = `${yyyy}-${mm}-${dd}`;
      newRow = {
        _id: currentData.length,
        id: '',
        email: '',
        name: '',
        product: '',
        start_date: yyyyMmDd,
        maturity_date: '', // period를 기반으로 서버에서 자동 계산되므로 빈 값으로 설정
        principal: '$0.0',
        minimum_lockup: '0',
        period: '0', // period 필드를 monthly_rate, annual_rate 앞으로 이동
        monthly_rate: '0.0',
        annual_rate: '0.0',
        partial_withdraw: '0.0', // partial_withdraw 필드 추가
        created_at: '',
        updated_at: ''
      };
    } else {
      // 기본(legacy)
      newRow = {
        _id: currentData.length,
        id: '',
        email: '',
        name: '',
        eth_balance: '0.0',
        btc_balance: '0.0',
        usdt_balance: '0.0',
        usdc_balance: '0.0',
        krw_balance: '0.0',
        veee_quantity: '0.0',
        vebe_quantity: '0.0',
        created_at: '',
        updated_at: ''
      };
    }
    
    if (previewData) {
      setPreviewData([...currentData, newRow]);
    } else {
      setCsvData([...currentData, newRow]);
    }
  };

  const deleteRow = (rowIndex) => {
    // 편집 모드가 활성화되어 있으면 강제로 종료
    if (editingCell) {
      setEditingCell(null);
      setEditValue('');
    }
    
    // 미리보기 모드인지 확인하여 올바른 데이터 소스 사용
    const currentData = previewData || csvData;
    const target = currentData[rowIndex];

    // id가 비어있으면 단순 삭제
    if (!target || !target.id) {
      const newData = currentData.filter((_, index) => index !== rowIndex);
      if (previewData) {
        setPreviewData(newData);
      } else {
        setCsvData(newData);
      }
    } else {
      const newData = [...currentData];
      if (dataType === 'preferred-return' || dataType === 'fixed-income') {
        // 특정 필드 초기화
        newData[rowIndex]['principal'] = '$0.0';
        newData[rowIndex]['minimum_lockup'] = '0';
        newData[rowIndex]['monthly_rate'] = '0.0';
        newData[rowIndex]['annual_rate'] = '0.0';
        newData[rowIndex]['partial_withdraw'] = '0.0';
      } else {
        const zeroFields = ['eth_balance','btc_balance','usdt_balance','usdc_balance','krw_balance','veee_quantity','vebe_quantity'];
        zeroFields.forEach((f) => newData[rowIndex][f] = '0.0');
      }
      if (previewData) {
        setPreviewData(newData);
      } else {
        setCsvData(newData);
      }
    }
  };

  const updateCsvData = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const currentData = previewData || csvData;

      // 1) 빈 이메일 존재 검사
      for (let i = 0; i < currentData.length; i++) {
        const email = (currentData[i].email || '').trim();
        if (!email) {
          showNotification(`행 ${i + 1}: 이메일은 필수입니다.`, 'error');
          return;
        }
        if (dataType === 'preferred-return' || dataType === 'fixed-income') {
          // product 비어있지 않아야 함
          if (!((currentData[i].product || '').trim())) {
            showNotification(`행 ${i + 1}: product는 필수입니다.`, 'error');
            return;
          }
          // principal 통화 형식: 선행 통화기호 + 숫자
          const p = (currentData[i].principal || '').trim();
          const currencyRegex = /^[$₩]|^[A-Z]{3}/; // 기호 또는 통화코드 시작
          const numberPart = p.replace(/^([$₩]|[A-Z]{3})\s*/, '');
          const numberRegex = /^\d+(\.\d+)?$/;
          if (!currencyRegex.test(p) || !numberRegex.test(numberPart)) {
            showNotification(`행 ${i + 1}: principal은 통화기호(예: $, ₩ 또는 통화코드)와 숫자 형식이어야 합니다.`, 'error');
            return;
          }
        }
      }

      const created = [];
      const updated = [];
      const skipped = [];
      const lotCreated = [];

      const getNum = (v) => {
        const n = parseFloat((v ?? '').toString().replace(/,/g, ''));
        return isNaN(n) ? 0 : n;
      };
      const hasLegacyDiff = (row, orig) => {
        if (!orig) return true;
        const fields = ['eth_balance','btc_balance','usdt_balance','usdc_balance','krw_balance','veee_quantity','vebe_quantity'];
        const numericChanged = fields.some(f => getNum(row[f]) !== getNum(orig[f]));
        const nameChanged = (row.name || '') !== (orig.name || '');
        return numericChanged || nameChanged;
      };

      if (dataType === 'preferred-return' || dataType === 'fixed-income') {
        const prGetNum = (v) => {
          // rates as numbers; principal kept as text (currency string)
          const n = parseFloat((v ?? '').toString().replace(/,/g, ''));
          return isNaN(n) ? 0 : n;
        };
        const hasPrDiff = (row, orig) => {
          if (!orig) return true;
          const productChanged = (row.product || '') !== (orig.product || '');
          const startChanged = (row.start_date || '') !== (orig.start_date || '');
          const maturityChanged = (row.maturity_date || '') !== (orig.maturity_date || '');
          const principalChanged = (row.principal || '') !== (orig.principal || '');
          const lockupChanged = (row.minimum_lockup || '') !== (orig.minimum_lockup || '');
          const monthlyChanged = prGetNum(row.monthly_rate) !== prGetNum(orig.monthly_rate);
          const annualChanged = prGetNum(row.annual_rate) !== prGetNum(orig.annual_rate);
          const nameChanged = (row.name || '') !== (orig.name || '');
          const periodChanged = prGetNum(row.period) !== prGetNum(orig.period);
          const partialWithdrawChanged = prGetNum(row.partial_withdraw) !== prGetNum(orig.partial_withdraw);
          return productChanged || startChanged || maturityChanged || principalChanged || lockupChanged || monthlyChanged || annualChanged || nameChanged || periodChanged || partialWithdrawChanged;
        };

        for (let i = 0; i < currentData.length; i++) {
          const row = currentData[i];
          const email = (row.email || '').trim();
          // fixed-income과 preferred-return patch 요청에 필요한 필드만 포함
          const body = {
            name: (row.name || '').trim(),
            product: (row.product || '').trim(),
            start_date: (row.start_date || '').trim(),
            principal: (row.principal || '').trim(),
            minimum_lockup: (row.minimum_lockup || '').toString().trim(),
            period: prGetNum(row.period).toString(), // period를 문자열로 변환
            monthly_rate: prGetNum(row.monthly_rate),
            annual_rate: prGetNum(row.annual_rate),
            partial_withdraw: prGetNum(row.partial_withdraw) // partial_withdraw 필드 추가
          };

          if (!row.id) {
            const createPath = dataType === 'preferred-return' ? 'preferred-return' : 'fixed-income';
            await axios.post(`/api/external/users/${encodeURIComponent(email)}/${createPath}/create`, body, {
              headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
            });
            created.push(email);
          } else {
            const orig = originalRowsById[row.id];
            if (hasPrDiff(row, orig)) {
              const patchPath = dataType === 'preferred-return' ? 'preferred-return' : 'fixed-income';
              await axios.patch(`/api/external/users/${patchPath}/${encodeURIComponent(row.id)}`, body, {
                headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
              });
              updated.push(email);
            } else {
              skipped.push(email);
            }
          }
        }
      } else {
        // legacy:
        // - 동일 email 다중 행 허용
        // - 저장 시 email 단위로 합산하여 기존 legacy 엔트리를 업데이트
        // - id가 없는 추가 행의 vebe/veee 값은 token lot으로 기록
        const byEmail = new Map();
        for (let i = 0; i < currentData.length; i++) {
          const row = currentData[i];
          const normalizedEmail = (row.email || '').trim().toLowerCase();
          if (!byEmail.has(normalizedEmail)) byEmail.set(normalizedEmail, []);
          byEmail.get(normalizedEmail).push(row);
        }

        for (const [email, rows] of byEmail.entries()) {
          const baseRow = rows.find((r) => !!r.id) || rows[0];
          const mergedName = rows.map((r) => (r.name || '').trim()).find((n) => n) || '';
          const mergedBody = rows.reduce((acc, r) => {
            acc.eth_balance += getNum(r.eth_balance);
            acc.btc_balance += getNum(r.btc_balance);
            acc.usdt_balance += getNum(r.usdt_balance);
            acc.usdc_balance += getNum(r.usdc_balance);
            acc.krw_balance += getNum(r.krw_balance);
            acc.veee_quantity += getNum(r.veee_quantity);
            acc.vebe_quantity += getNum(r.vebe_quantity);
            return acc;
          }, {
            eth_balance: 0,
            btc_balance: 0,
            usdt_balance: 0,
            usdc_balance: 0,
            krw_balance: 0,
            veee_quantity: 0,
            vebe_quantity: 0
          });

          // 기존 사용자(기존 legacy row id 존재)에서만 추가 행을 token lot으로 취급한다.
          // 새 사용자 생성 케이스는 우선 기존 단일 row 동작을 유지한다.
          if (baseRow?.id) {
            const nowIso = new Date().toISOString();
            const extraRows = rows.filter((r) => !r.id && r !== baseRow);
            const lotFieldMap = [
              { symbol: 'BTC', field: 'btc_balance' },
              { symbol: 'ETH', field: 'eth_balance' },
              { symbol: 'USDT', field: 'usdt_balance' },
              { symbol: 'USDC', field: 'usdc_balance' },
              { symbol: 'KRW', field: 'krw_balance' },
              { symbol: 'VEBE', field: 'vebe_quantity' },
              { symbol: 'VEEE', field: 'veee_quantity' }
            ];
            for (const row of extraRows) {
              for (const item of lotFieldMap) {
                const quantity = getNum(row[item.field]);
                if (quantity <= 0) continue;
                await axios.post(`/api/external/users/${encodeURIComponent(email)}/legacy/lots`, {
                  symbol: item.symbol,
                  quantity,
                  received_at: nowIso,
                  note: 'added-from-data-management'
                }, {
                  headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
                });
                lotCreated.push(`${email}:${item.symbol}`);
              }
            }
          }

          if (!baseRow?.id) {
            await axios.post(`/api/external/users/${encodeURIComponent(email)}/legacy/create`, {
              ...mergedBody,
              name: mergedName
            }, {
              headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
            });
            created.push(email);
          } else {
            const orig = originalRowsById[baseRow.id];
            const mergedLikeRow = { ...baseRow, ...mergedBody, name: mergedName };
            if (hasLegacyDiff(mergedLikeRow, orig)) {
              await axios.patch(`/api/external/users/legacy/${encodeURIComponent(baseRow.id)}`, {
                ...mergedBody,
                name: mergedName
              }, {
                headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
              });
              updated.push(email);
            } else {
              skipped.push(email);
            }
          }
        }
      }

      // 성공 메시지 및 재로드
      const messages = [];
      if (created.length) messages.push(`생성: ${created.length}건`);
      if (updated.length) messages.push(`업데이트: ${updated.length}건`);
      if (lotCreated.length) messages.push(`Token lot 생성: ${lotCreated.length}건`);
      if (skipped.length) messages.push(`변경 없음: ${skipped.length}건`);
      showMultiLineNotification(messages.length ? messages : ['변경사항이 없습니다.'], 'success');

      await loadCsvData();
    } catch (error) {
      console.error('Error saving legacy data:', error);
      const msg = error.response?.data?.message || error.message || '저장 중 오류가 발생했습니다.';
      showNotification(msg, 'error');
    }
  };

  const handleNodeClick = async (node) => {
    setSelectedHash(node.hash);
    
    // 형제 노드가 있는지 확인하고 설정
    if (node.siblings && node.siblings.length > 0) {
      setCurrentSiblings(node.siblings);
      setSelectedSibling(node.hash); // 기본값은 현재 노드
    } else {
      setCurrentSiblings([]);
      setSelectedSibling(null);
    }
    
    await loadCsvData(node.hash);
    
    // 히스토리 구조는 변경하지 않고 그대로 유지
    // 단순히 선택된 노드의 데이터만 미리보기로 표시
  };

  // 히스토리 팝업 내에서 특정 박스 클릭 시 해당 박스의 데이터만 미리보기
  const handleHistoryBoxClick = async (node) => {
    setSelectedHash(node.hash);
    await loadCsvData(node.hash);
    
    // 형제 노드가 있는지 확인하고 설정
    if (node.siblings && node.siblings.length > 0) {
      setCurrentSiblings(node.siblings);
      setSelectedSibling(node.hash); // 기본값은 현재 노드
    } else {
      setCurrentSiblings([]);
      setSelectedSibling(null);
    }
    
    // 히스토리 구조는 변경하지 않고 그대로 유지
    // 단순히 선택된 노드의 데이터만 미리보기로 표시
  };

  // checkout 관련 기능 제거됨

  const goToDashboard = (userEmail, dataType) => {
    // URL 파라미터를 통해 사용자 정보를 전달하여 대시보드로 이동
    const params = new URLSearchParams();
    params.append('email', userEmail);
    params.append('dataType', dataType);
    
    // 미리보기 모드일 때만 해시 ID 포함
    if (selectedHash && selectedHash !== currentHash) {
      params.append('hash_id', selectedHash);
    }
    
    navigate(`/dashboard?${params.toString()}`);
  };

  const goToCurrentCheckout = async () => {
    // 편집 모드가 활성화되어 있으면 강제로 종료
    if (editingCell) {
      setEditingCell(null);
      setEditValue('');
    }
    
    try {
      setSelectedHash(currentHash);
      setSelectedSibling(null);
      setCurrentSiblings([]);
      await loadCsvData(currentHash);
    } catch (error) {
      console.error('Error going to current checkout:', error);
      showNotification('현재 체크아웃으로 이동하는데 실패했습니다.', 'error');
    }
  };

  const handleSiblingChange = async (siblingHash) => {
    setSelectedSibling(siblingHash);
    await loadCsvData(siblingHash);
    
    // 선택된 형제 노드의 전체 서브트리를 다시 로드
    try {
      const token = localStorage.getItem('authToken');
      
      // URL 파라미터에서 데이터 타입 확인 (우선순위)
      const urlDataType = searchParams.get('dataType');
      const effectiveDataType = urlDataType || dataType;
      
      const response = await axios.get(`/api/external/csv-data/${effectiveDataType}/history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const historyData = response.data.history || response.data.chain || [];
      
      // 선택된 형제 노드를 찾기
      const siblingNode = historyData.find(item => item.hash === siblingHash);
      if (siblingNode) {
        // 선택된 노드의 부모들을 찾아서 경로 구성
        const buildPathToSibling = (targetHash) => {
          const path = [];
          let current = historyData.find(item => item.hash === targetHash);
          
          while (current) {
            path.unshift(current);
            current = historyData.find(item => item.hash === current.previous_hash);
          }
          
          return path;
        };
        
        // 선택된 노드까지의 경로와 그 자식들을 포함하는 새로운 히스토리 구성
        const pathToSibling = buildPathToSibling(siblingHash);
        const newHistoryData = [...pathToSibling];
        
        // 선택된 노드의 자식들을 재귀적으로 찾아서 추가
        const findChildren = (parentHash) => {
          const children = historyData.filter(item => item.previous_hash === parentHash);
          children.forEach(child => {
            if (!newHistoryData.find(item => item.hash === child.hash)) {
              newHistoryData.push(child);
              findChildren(child.hash);
            }
          });
        };
        
        findChildren(siblingHash);
        
        // 새로운 히스토리 데이터로 블록 라인 구성
        const blockLines = buildHistoryBlockLines(newHistoryData);
        setHistory(blockLines);
      }
    } catch (error) {
      console.error('Error reloading history for sibling:', error);
      showNotification('형제 노드의 히스토리를 다시 로드하는데 실패했습니다.', 'error');
    }
  };

  // 형제 노드 선택 팝업 열기
  const handleSiblingIndicatorClick = (node, event) => {
    event.stopPropagation(); // 이벤트 버블링 방지
    setSiblingPopupNode(node);
    setShowSiblingPopup(true);
  };

  // 형제 노드 선택 팝업에서 노드 선택
  const handleSiblingPopupSelect = async (selectedNode) => {
    setShowSiblingPopup(false);
    setSiblingPopupNode(null);
    
    // 선택된 형제 노드로 변경
    setSelectedHash(selectedNode.hash);
    await loadCsvData(selectedNode.hash);
    
    // 형제 노드 정보 설정
    if (selectedNode.siblings && selectedNode.siblings.length > 0) {
      setCurrentSiblings(selectedNode.siblings);
      setSelectedSibling(selectedNode.hash);
    } else {
      setCurrentSiblings([]);
      setSelectedSibling(null);
    }
    
    // 히스토리를 다시 로드하여 형제 관계 정보를 포함한 새로운 체인을 표시
    try {
      const token = localStorage.getItem('authToken');
      
      // URL 파라미터에서 데이터 타입 확인 (우선순위)
      const urlDataType = searchParams.get('dataType');
      const effectiveDataType = urlDataType || dataType;
      
      const response = await axios.get(`/api/external/csv-data/${effectiveDataType}/history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const historyData = response.data.history || response.data.chain || [];
      
      // 선택된 형제 노드를 찾기
      const siblingNode = historyData.find(item => item.hash === selectedNode.hash);
      if (siblingNode) {
        // 선택된 노드의 부모들을 찾아서 경로 구성
        const buildPathToSibling = (targetHash) => {
          const path = [];
          let current = historyData.find(item => item.hash === targetHash);
          
          while (current) {
            path.unshift(current);
            current = historyData.find(item => item.hash === current.previous_hash);
          }
          
          return path;
        };
        
        // 선택된 노드까지의 경로와 그 자식들을 포함하는 새로운 히스토리 구성
        const pathToSibling = buildPathToSibling(selectedNode.hash);
        const newHistoryData = [...pathToSibling];
        
        // 선택된 노드의 자식들을 재귀적으로 찾아서 추가
        const findChildren = (parentHash) => {
          const children = historyData.filter(item => item.previous_hash === parentHash);
          children.forEach(child => {
            if (!newHistoryData.find(item => item.hash === child.hash)) {
              newHistoryData.push(child);
              findChildren(child.hash);
            }
          });
        };
        
        findChildren(selectedNode.hash);
        
        // 전체 히스토리에서 형제 관계 정보를 가져와서 새로운 히스토리에 적용
        const blockLines = buildHistoryBlockLinesWithSiblings(newHistoryData, historyData);
        setHistory(blockLines);
      }
    } catch (error) {
      console.error('Error reloading history for selected sibling:', error);
      showNotification('선택된 형제 노드의 히스토리를 다시 로드하는데 실패했습니다.', 'error');
    }
  };

  // 사용자 관리 함수들
  const loadUsers = async () => {
    setLoadingUsers(true);
    try {
      const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
      console.log('Load users - Token:', token ? 'Present' : 'Missing');
      
      const response = await axios.get('/api/admin/users', {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setUsers(response.data.users);
      } else {
        showNotification('사용자 목록을 불러오는데 실패했습니다.', 'error');
      }
    } catch (error) {
      console.error('Error loading users:', error);
      showNotification('사용자 목록을 불러오는데 실패했습니다.', 'error');
    } finally {
      setLoadingUsers(false);
    }
  };

  const syncUsers = async () => {
    setSyncingUsers(true);
    try {
      const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
      console.log('Sync users - Token:', token ? 'Present' : 'Missing');
      
      const response = await axios.post('/api/admin/users/sync', {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('Sync users - Response:', response.data);
      
      if (response.data.success) {
        const results = response.data.results;
        showMultiLineNotification(
          [`동기화 완료: ${results.new_users_created}명 새로 생성, ${results.existing_users_updated}명 업데이트, ${results.failed_users}명 실패`],
          'success'
        );
        await loadUsers(); // 사용자 목록 새로고침
      } else {
        showNotification('사용자 동기화에 실패했습니다.', 'error');
      }
    } catch (error) {
      console.error('Error syncing users:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      
      if (error.response?.status === 403) {
        showNotification('관리자 권한이 필요합니다.', 'error');
      } else if (error.response?.status === 401) {
        showNotification('인증이 필요합니다. 다시 로그인해주세요.', 'error');
      } else if (error.response?.data?.message) {
        showMultiLineNotification([`동기화 실패: ${error.response.data.message}`], 'error');
      } else {
        showNotification('사용자 동기화에 실패했습니다.', 'error');
      }
    } finally {
      setSyncingUsers(false);
    }
  };

  const updateUser = async (email, userData) => {
    try {
      const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
      const response = await axios.put(`/api/admin/users/${encodeURIComponent(email)}`, userData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        showNotification('사용자 정보가 업데이트되었습니다.', 'success');
        await loadUsers(); // 사용자 목록 새로고침
        return true;
      } else {
        showNotification('사용자 정보 업데이트에 실패했습니다.', 'error');
        return false;
      }
    } catch (error) {
      console.error('Error updating user:', error);
      showNotification('사용자 정보 업데이트에 실패했습니다.', 'error');
      return false;
    }
  };

  const deleteUser = async (email) => {
    if (!window.confirm(`정말로 사용자 ${email}을 삭제하시겠습니까?`)) {
      return;
    }
    
    try {
      const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
      const response = await axios.delete(`/api/admin/users/${encodeURIComponent(email)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        showNotification('사용자가 삭제되었습니다.', 'success');
        await loadUsers(); // 사용자 목록 새로고침
      } else {
        showNotification('사용자 삭제에 실패했습니다.', 'error');
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      showNotification('사용자 삭제에 실패했습니다.', 'error');
    }
  };

  const refreshUserData = async (email) => {
    try {
      const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
      const response = await axios.post(`/api/admin/users/${encodeURIComponent(email)}/refresh`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        showNotification('사용자 데이터가 새로고침되었습니다.', 'success');
        await loadUsers(); // 사용자 목록 새로고침
      } else {
        showNotification('사용자 데이터 새로고침에 실패했습니다.', 'error');
      }
    } catch (error) {
      console.error('Error refreshing user data:', error);
      showNotification('사용자 데이터 새로고침에 실패했습니다.', 'error');
    }
  };

  const openUserEditModal = (user) => {
    setEditingUser({ ...user });
    setShowUserEditModal(true);
  };

  const closeUserEditModal = () => {
    setEditingUser(null);
    setShowUserEditModal(false);
  };

  const handleUserDataTypeChange = (event) => {
    const selectedOptions = Array.from(event.target.options).filter(opt => opt.selected).map(opt => opt.value);
    const originalUser = users.find(u => u.email === editingUser.email);
    
    if (originalUser) {
      const removedTypes = originalUser.available_data_types.filter(
        type => !selectedOptions.includes(type)
      );
      
      if (removedTypes.length > 0) {
        const warningMessage = `경고: 다음 데이터 타입이 제거됩니다:\n${removedTypes.join(', ')}\n\n이 데이터 타입에 실제 데이터가 있을 수 있습니다.`;
        showNotification(warningMessage, 'warning');
      }
    }
    
    setEditingUser({ ...editingUser, available_data_types: selectedOptions });
  };

  const handleCheckboxChange = (dataType, checked) => {
    const originalUser = users.find(u => u.email === editingUser.email);
    let newAvailableTypes;
    
    if (checked) {
      // 체크박스가 체크된 경우 - 데이터 타입 추가
      newAvailableTypes = [...editingUser.available_data_types, dataType];
    } else {
      // 체크박스가 해제된 경우 - 데이터 타입 제거
      newAvailableTypes = editingUser.available_data_types.filter(type => type !== dataType);
    }
    
    setEditingUser({ ...editingUser, available_data_types: newAvailableTypes });
  };

  const saveUserEdit = async () => {
    if (!editingUser) return;
    
    // 데이터 타입 제거 방지 체크
    const originalUser = users.find(u => u.email === editingUser.email);
    if (originalUser) {
      const removedTypes = originalUser.available_data_types.filter(
        type => !editingUser.available_data_types.includes(type)
      );
      
      if (removedTypes.length > 0) {
        const confirmMessage = `다음 데이터 타입을 제거하려고 합니다:\n${removedTypes.join(', ')}\n\n이 데이터 타입에 실제 데이터가 있을 수 있습니다. 정말로 제거하시겠습니까?`;
        if (!window.confirm(confirmMessage)) {
          return;
        }
      }
    }
    
    const success = await updateUser(editingUser.email, {
      name: editingUser.name,
      available_data_types: editingUser.available_data_types
    });
    
    if (success) {
      closeUserEditModal();
    }
  };

  const handleUserSort = (field) => {
    if (userSortField === field) {
      setUserSortDirection(userSortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setUserSortField(field);
      setUserSortDirection('asc');
    }
  };

  const getFilteredAndSortedUsers = () => {
    let filteredUsers = users;
    
    // 검색 필터링
    if (userSearchTerm) {
      filteredUsers = users.filter(user => 
        user.email.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
        user.name.toLowerCase().includes(userSearchTerm.toLowerCase())
      );
    }
    
    // 정렬
    filteredUsers.sort((a, b) => {
      let aValue = a[userSortField];
      let bValue = b[userSortField];
      
      if (userSortField === 'created_at' || userSortField === 'updated_at') {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      }
      
      if (aValue < bValue) return userSortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return userSortDirection === 'asc' ? 1 : -1;
      return 0;
    });
    
    return filteredUsers;
  };

  const legacyFlowUsers = users.filter((u) => (u.available_data_types || []).includes('legacy'));
  const fixedIncomeFlowUsers = users.filter((u) => (u.available_data_types || []).includes('fixed-income'));

  const loadLegacyFlowHistory = async (targetEmail = legacyFlowEmail, targetLimit = legacyFlowLimit) => {
    if (!targetEmail) {
      setLegacyFlowEvents([]);
      return;
    }
    setLoadingLegacyFlow(true);
    try {
      const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
      const response = await axios.get(
        `/api/external/users/${encodeURIComponent(targetEmail)}/legacy/asset-flow-history`,
        {
          params: { limit: targetLimit },
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      setLegacyFlowEvents(Array.isArray(response.data?.events) ? response.data.events : []);
    } catch (error) {
      console.error('Error loading legacy flow history:', error);
      setLegacyFlowEvents([]);
      showNotification('Legacy 입출 히스토리를 불러오는데 실패했습니다.', 'error');
    } finally {
      setLoadingLegacyFlow(false);
    }
  };

  const loadFixedIncomeDepositHistory = async (targetEmail = fixedIncomeFlowEmail, targetLimit = fixedIncomeFlowLimit) => {
    if (!targetEmail) {
      setFixedIncomeFlowEvents([]);
      return;
    }
    setLoadingFixedIncomeFlow(true);
    try {
      const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
      const response = await axios.get(
        `/api/external/users/${encodeURIComponent(targetEmail)}/fixed-income/deposit-history`,
        {
          params: { limit: targetLimit },
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      setFixedIncomeFlowEvents(Array.isArray(response.data?.events) ? response.data.events : []);
    } catch (error) {
      console.error('Error loading fixed income deposit history:', error);
      setFixedIncomeFlowEvents([]);
      showNotification('Fixed Income 입출 히스토리를 불러오는데 실패했습니다.', 'error');
    } finally {
      setLoadingFixedIncomeFlow(false);
    }
  };

  const formatDelta = (value, decimals = 8) => {
    const n = Number(value || 0);
    if (!Number.isFinite(n) || Math.abs(n) < 1e-9) return '-';
    const sign = n > 0 ? '+' : '';
    return `${sign}${n.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: decimals })}`;
  };

  const buildYearMonthRange = (startYm, endYm) => {
    const months = [];
    let year = Number(startYm.slice(0, 4));
    let month = Number(startYm.slice(4, 6));
    const endYear = Number(endYm.slice(0, 4));
    const endMonth = Number(endYm.slice(4, 6));

    while (year < endYear || (year === endYear && month <= endMonth)) {
      months.push(`${year}${String(month).padStart(2, '0')}`);
      month += 1;
      if (month > 12) {
        month = 1;
        year += 1;
      }
    }
    return months;
  };

  const loadVeTokenPrices = async () => {
    setLoadingVePrices(true);
    try {
      const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
      const response = await axios.get('/api/external/legacy/ve-token-prices', {
        params: { limit_months: 60 },
        headers: { Authorization: `Bearer ${token}` }
      });
      const rows = Array.isArray(response.data?.rows) ? response.data.rows : [];
      const normalizedRows = rows.map((row) => ({
        ...row,
        vebe_price: row.vebe_price ?? '',
        veee_price: row.veee_price ?? ''
      }));

      // 2025-07부터 현재 월까지 항상 입력 가능한 행을 보장
      const monthMap = new Map(normalizedRows.map((row) => [row.year_month, row]));
      const now = new Date();
      const currentYm = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}`;
      const requiredMonths = buildYearMonthRange('202507', currentYm);

      requiredMonths.forEach((ym) => {
        if (!monthMap.has(ym)) {
          monthMap.set(ym, {
            year_month: ym,
            vebe_price: '',
            veee_price: '',
            updated_at: null,
          });
        }
      });

      const mergedRows = Array.from(monthMap.values()).sort((a, b) =>
        String(b.year_month).localeCompare(String(a.year_month))
      );
      setVePriceRows(mergedRows);
    } catch (error) {
      console.error('Error loading VE token prices:', error);
      setVePriceRows([]);
      showNotification('VE 토큰 가격을 불러오는데 실패했습니다.', 'error');
    } finally {
      setLoadingVePrices(false);
    }
  };

  const handleVePriceCellChange = (yearMonth, field, value) => {
    setVePriceRows((prev) =>
      prev.map((row) => (row.year_month === yearMonth ? { ...row, [field]: value } : row))
    );
  };

  const saveVePriceRow = async (row) => {
    const ym = row.year_month;
    const vebe = Number(row.vebe_price);
    const veee = Number(row.veee_price);
    if (!Number.isFinite(vebe) || vebe <= 0 || !Number.isFinite(veee) || veee <= 0) {
      showNotification('VEBE/VEEE 가격은 0보다 큰 숫자여야 합니다.', 'error');
      return;
    }
    setSavingVePriceMonth(ym);
    try {
      const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
      await axios.patch(`/api/external/legacy/ve-token-prices/${encodeURIComponent(ym)}`, {
        vebe_price: vebe,
        veee_price: veee
      }, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      showNotification(`${ym} 가격이 저장되었습니다.`, 'success');
      await loadVeTokenPrices();
    } catch (error) {
      console.error('Error saving VE token prices:', error);
      showNotification('VE 토큰 가격 저장에 실패했습니다.', 'error');
    } finally {
      setSavingVePriceMonth(null);
    }
  };

  const renderSubPage = () => {
    switch (currentSubPage) {
      case 'data-management':
        return (
          <div className="sub-page-content">
            <div className="control-panel">
              <div className="control-left">
                <select
                  className="data-type-select"
                  value={dataType}
                  onChange={(e) => handleDataTypeChange(e.target.value)}
                >
                  <option value="legacy">Legacy</option>
                  <option value="preferred-return">Preferred Return</option>
                  <option value="fixed-income">Fixed Income</option>
                </select>
                
                {/* 고급 모드에서만 표시되는 버튼들 */}
                <button 
                  className="control-button add-button" 
                  onClick={addRow}
                  title="새로운 데이터 행을 추가합니다"
                >
                  <span className="button-icon">➕</span>
                  행 추가
                </button>
                
                <button 
                  className="control-button update-button" 
                  onClick={updateCsvData}
                  title="데이터를 저장하고 즉시 반영합니다"
                >
                  <span className="button-icon">💾</span>
                  저장 및 반영
                </button>
              </div>
              
              <div className="control-right">
              </div>
            </div>

            <div className="table-container data-management-table-container">
              {(previewData || csvData).length > 0 && (previewData || csvData)[0] ? (
                <table className="data-table">
                  <thead>
                    <tr>
                      {Object.keys((previewData || csvData)[0])
                        .filter(key => key !== '_id')
                        .filter(header => {
                          // preferred-return 또는 fixed-income 데이터 타입일 때 Interest Amount와 Current Balance 필드 숨기기
                          if ((dataType === 'preferred-return' || dataType === 'fixed-income') && 
                              (header === 'Interest Amount' || header === 'Current Balance')) {
                            return false;
                          }
                          // period와 partial_withdraw 필드는 preferred-return과 fixed-income에서만 표시
                          if ((header === 'period' || header === 'partial_withdraw') && dataType === 'legacy') {
                            return false;
                          }
                          return true;
                        })
                        .map((header, index) => (
                          <th key={index}>{header}</th>
                        ))}
                      <th>작업</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(previewData || csvData).map((row, rowIndex) => {
                      const headers = Object.keys(row)
                        .filter(key => key !== '_id')
                        .filter(header => {
                          // preferred-return 또는 fixed-income 데이터 타입일 때 Interest Amount와 Current Balance 필드 숨기기
                          if ((dataType === 'preferred-return' || dataType === 'fixed-income') && 
                              (header === 'Interest Amount' || header === 'Current Balance')) {
                            return false;
                          }
                          // period와 partial_withdraw 필드는 preferred-return과 fixed-income에서만 표시
                          if ((header === 'period' || header === 'partial_withdraw') && dataType === 'legacy') {
                            return false;
                          }
                          return true;
                        });
                      const isPreviewMode = selectedHash && selectedHash !== currentHash;
                      return (
                        <tr key={rowIndex}>
                          {headers.map((header, colIndex) => {
                            const value = row[header] || '';
                            const isEmail = header.toLowerCase().includes('email');
                            const isDate = header.toLowerCase().includes('date');
                            const isCurrency = header.toLowerCase().includes('balance') || 
                                              header.toLowerCase().includes('principal') || 
                                              header.toLowerCase().includes('amount') ||
                                              header.toLowerCase().includes('rate');
                            const isNumeric = header.toLowerCase().includes('quantity') || 
                                           header.toLowerCase().includes('rate') ||
                                           header.toLowerCase().includes('lockup') ||
                                           header.toLowerCase().includes('period') ||
                                           header === 'partial_withdraw';
                            
                            let cellClassName = 'editable-cell';
                            if (isEmail) cellClassName += ' email-cell';
                            if (isDate) cellClassName += ' date-cell';
                            if (isCurrency) cellClassName += ' currency-cell';
                            if (isNumeric) cellClassName += ' numeric-cell';
                            
                            // preferred-return과 fixed-income에서 maturity_date는 편집 불가
                            if ((dataType === 'preferred-return' || dataType === 'fixed-income') && header === 'maturity_date') {
                              cellClassName = 'readonly-cell';
                            }
                            
                            return (
                              <td key={colIndex} className="table-cell">
                                {header === 'id' ? (
                                  <div className="editable-cell id-cell" title={value}>{value}</div>
                                ) : editingCell?.rowIndex === rowIndex && editingCell?.column === header ? (
                                  <input
                                    type="text"
                                    value={editValue}
                                    onChange={(e) => setEditValue(e.target.value)}
                                    onBlur={finishEditing}
                                    onKeyPress={(e) => {
                                      if (e.key === 'Enter') finishEditing();
                                    }}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Escape') {
                                        setEditingCell(null);
                                        setEditValue('');
                                      }
                                    }}
                                    className="cell-input"
                                    placeholder={getPlaceholder(header)}
                                    autoFocus
                                  />
                                ) : (
                                  <div
                                    onClick={() => startEditing(rowIndex, header)}
                                    className={cellClassName}
                                    title={value}
                                  >
                                    {value}
                                  </div>
                                )}
                              </td>
                            );
                          })}
                          <td className="table-cell">
                            <div className="action-buttons">
                              {/* 대시보드 보기 버튼 - 미리보기 모드가 아닐 때는 기존 로직 사용 */}
                              <button
                                onClick={() => {
                                  // 선택된 사용자의 대시보드로 이동
                                  const userEmail = row.email || row.user;
                                  if (userEmail) {
                                    goToDashboard(userEmail, dataType);
                                  }
                                }}
                                onContextMenu={(e) => {
                                  // 우클릭 시 새 탭에서 열기
                                  e.preventDefault();
                                  const userEmail = row.email || row.user;
                                  if (userEmail) {
                                    const params = new URLSearchParams();
                                    params.append('email', userEmail);
                                    params.append('dataType', dataType);
                                    
                                    // 미리보기 모드일 때만 해시 ID 포함
                                    if (selectedHash && selectedHash !== currentHash) {
                                      params.append('hash_id', selectedHash);
                                    }
                                    
                                    const dashboardUrl = `/dashboard?${params.toString()}`;
                                    window.open(dashboardUrl, '_blank');
                                  }
                                }}
                                className="action-button dashboard-button"
                                onMouseEnter={(e) => showTooltip("사용자의 대시보드를 확인합니다 (우클릭: 새 탭에서 열기)", e)}
                                onMouseLeave={hideTooltip}
                              >
                                📊
                              </button>
                              <button
                                onClick={() => deleteRow(rowIndex)}
                                className="action-button delete-button"
                                onMouseEnter={(e) => showTooltip("이 행을 삭제합니다", e)}
                                onMouseLeave={hideTooltip}
                              >
                                🗑️
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              ) : (
                <div className="no-data">
                  <p>데이터가 없습니다. (헤더: {(previewData || csvData).length > 0 ? Object.keys((previewData || csvData)[0]).filter(key => key !== '_id').length : 0}, 데이터: {(previewData || csvData).length})</p>
                </div>
              )}
            </div>
          </div>
        );
      case 'custom-analysis-pages':
        return <CustomAnalysisPageManager showNotification={showNotification} />;
      case 've-token-prices':
        return (
          <div className="sub-page-content">
            <div className="ve-price-header">
              <h2>🪙 VEBE / VEEE 월별 환산 가격</h2>
              <div className="ve-price-header-actions">
                <button className="control-button update-button" onClick={loadVeTokenPrices} disabled={loadingVePrices}>
                  {loadingVePrices ? '불러오는 중...' : '새로고침'}
                </button>
              </div>
            </div>
            <div className="ve-price-description">
              새로운 달이 시작되면 전월 값을 복사한 행이 자동 생성됩니다. 수정 전까지는 전월 값이 적용됩니다.
            </div>
            <div className="table-container">
              <table className="data-table ve-price-table">
                <thead>
                  <tr>
                    <th>월(YYYYMM)</th>
                    <th>VEBE - BTC 환산</th>
                    <th>VEEE - ETH 환산</th>
                    <th>최근 수정</th>
                    <th>작업</th>
                  </tr>
                </thead>
                <tbody>
                  {loadingVePrices ? (
                    <tr>
                      <td colSpan="5" className="loading-row">가격 데이터를 불러오는 중입니다...</td>
                    </tr>
                  ) : vePriceRows.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="no-data-row">데이터가 없습니다.</td>
                    </tr>
                  ) : (
                    vePriceRows.map((row) => (
                      <tr key={row.year_month}>
                        <td>{row.year_month}</td>
                        <td>
                          <input
                            className="ve-price-input"
                            value={row.vebe_price}
                            onChange={(e) => handleVePriceCellChange(row.year_month, 'vebe_price', e.target.value)}
                          />
                        </td>
                        <td>
                          <input
                            className="ve-price-input"
                            value={row.veee_price}
                            onChange={(e) => handleVePriceCellChange(row.year_month, 'veee_price', e.target.value)}
                          />
                        </td>
                        <td>{row.updated_at ? new Date(row.updated_at).toLocaleString() : '-'}</td>
                        <td>
                          <button
                            className="action-button edit-button"
                            onClick={() => saveVePriceRow(row)}
                            disabled={savingVePriceMonth === row.year_month}
                          >
                            {savingVePriceMonth === row.year_month ? '저장중' : '저장'}
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        );
      case 'legacy-flow-history':
        return (
          <div className="sub-page-content">
            <div className="legacy-flow-header">
              <h2>🧾 Legacy 자산 입출 히스토리</h2>
              <div className="legacy-flow-controls">
                <select
                  className="data-type-select"
                  value={legacyFlowEmail}
                  onChange={(e) => setLegacyFlowEmail(e.target.value)}
                >
                  <option value="">유저 이메일 선택</option>
                  {legacyFlowUsers.map((u) => (
                    <option key={u.email} value={u.email}>{u.email}</option>
                  ))}
                </select>
                <select
                  className="data-type-select"
                  value={legacyFlowLimit}
                  onChange={(e) => setLegacyFlowLimit(Number(e.target.value))}
                >
                  <option value={100}>최근 100건</option>
                  <option value={300}>최근 300건</option>
                  <option value={500}>최근 500건</option>
                </select>
                <button
                  className="control-button update-button"
                  onClick={() => loadLegacyFlowHistory(legacyFlowEmail, legacyFlowLimit)}
                  disabled={!legacyFlowEmail || loadingLegacyFlow}
                >
                  {loadingLegacyFlow ? '불러오는 중...' : '조회'}
                </button>
              </div>
            </div>

            <div className="table-container">
              <table className="data-table legacy-flow-table">
                <thead>
                  <tr>
                    <th>시각</th>
                    <th>타입</th>
                    <th>BTC</th>
                    <th>ETH</th>
                    <th>USDT</th>
                    <th>USDC</th>
                    <th>KRW</th>
                    <th>VEBE</th>
                    <th>VEEE</th>
                    <th>사유</th>
                  </tr>
                </thead>
                <tbody>
                  {loadingLegacyFlow ? (
                    <tr>
                      <td colSpan="10" className="loading-row">Legacy 입출 히스토리를 불러오는 중입니다...</td>
                    </tr>
                  ) : !legacyFlowEmail ? (
                    <tr>
                      <td colSpan="10" className="no-data-row">조회할 유저 이메일을 선택해주세요.</td>
                    </tr>
                  ) : legacyFlowEvents.length === 0 ? (
                    <tr>
                      <td colSpan="10" className="no-data-row">입출 히스토리 데이터가 없습니다.</td>
                    </tr>
                  ) : (
                    legacyFlowEvents.map((event) => (
                      <tr key={`${event.history_id}-${event.created_at}`}>
                        <td>{event.created_at ? new Date(event.created_at).toLocaleString() : '-'}</td>
                        <td>
                          <span className={`flow-type-badge ${event.flow_type || 'mixed'}`}>
                            {event.flow_type === 'deposit' ? '입금' : event.flow_type === 'withdrawal' ? '출금' : '혼합'}
                          </span>
                        </td>
                        <td>{formatDelta(event?.deltas?.BTC, 8)}</td>
                        <td>{formatDelta(event?.deltas?.ETH, 8)}</td>
                        <td>{formatDelta(event?.deltas?.USDT, 8)}</td>
                        <td>{formatDelta(event?.deltas?.USDC, 8)}</td>
                        <td>{formatDelta(event?.deltas?.KRW, 2)}</td>
                        <td>{formatDelta(event?.deltas?.VEBE, 8)}</td>
                        <td>{formatDelta(event?.deltas?.VEEE, 8)}</td>
                        <td className="flow-reason-cell">{event.change_reason || '-'}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        );
      case 'fixed-income-deposit-history':
        return (
          <div className="sub-page-content">
            <div className="legacy-flow-header">
              <h2>🏦 Fixed Income 입출 히스토리</h2>
              <div className="legacy-flow-controls">
                <select
                  className="data-type-select"
                  value={fixedIncomeFlowEmail}
                  onChange={(e) => setFixedIncomeFlowEmail(e.target.value)}
                >
                  <option value="">유저 이메일 선택</option>
                  {fixedIncomeFlowUsers.map((u) => (
                    <option key={u.email} value={u.email}>{u.email}</option>
                  ))}
                </select>
                <select
                  className="data-type-select"
                  value={fixedIncomeFlowLimit}
                  onChange={(e) => setFixedIncomeFlowLimit(Number(e.target.value))}
                >
                  <option value={100}>최근 100건</option>
                  <option value={300}>최근 300건</option>
                  <option value={500}>최근 500건</option>
                </select>
                <button
                  className="control-button update-button"
                  onClick={() => loadFixedIncomeDepositHistory(fixedIncomeFlowEmail, fixedIncomeFlowLimit)}
                  disabled={!fixedIncomeFlowEmail || loadingFixedIncomeFlow}
                >
                  {loadingFixedIncomeFlow ? '불러오는 중...' : '조회'}
                </button>
              </div>
            </div>

            <div className="table-container">
              <table className="data-table legacy-flow-table">
                <thead>
                  <tr>
                    <th>시작 시각</th>
                    <th>이름</th>
                    <th>상품</th>
                    <th>원금</th>
                    <th>만기일</th>
                    <th>타입</th>
                    <th>자산</th>
                    <th>금액</th>
                    <th>사유</th>
                  </tr>
                </thead>
                <tbody>
                  {loadingFixedIncomeFlow ? (
                    <tr>
                      <td colSpan="9" className="loading-row">Fixed Income 입출 히스토리를 불러오는 중입니다...</td>
                    </tr>
                  ) : !fixedIncomeFlowEmail ? (
                    <tr>
                      <td colSpan="9" className="no-data-row">조회할 유저 이메일을 선택해주세요.</td>
                    </tr>
                  ) : fixedIncomeFlowEvents.length === 0 ? (
                    <tr>
                      <td colSpan="9" className="no-data-row">입출 히스토리 데이터가 없습니다.</td>
                    </tr>
                  ) : (
                    fixedIncomeFlowEvents.map((event) => (
                      <tr key={`${event.history_id}-${event.created_at}`}>
                        <td>{event.created_at ? new Date(event.created_at).toLocaleString() : '-'}</td>
                        <td>{event.name || '-'}</td>
                        <td>{event.product || '-'}</td>
                        <td>{event.principal || '-'}</td>
                        <td>{event.maturity_date ? new Date(event.maturity_date).toLocaleString() : '-'}</td>
                        <td>
                          <span className={`flow-type-badge ${event.flow_type || 'mixed'}`}>
                            {event.flow_type === 'withdrawal' ? '출금' : '입금'}
                          </span>
                        </td>
                        <td>{event.flow_asset || '-'}</td>
                        <td>{formatDelta(event.flow_type === 'withdrawal' ? -(Number(event.flow_amount || 0)) : Number(event.flow_amount || 0), event.flow_asset === 'KRW' ? 2 : 8)}</td>
                        <td className="flow-reason-cell">{event.change_reason || '-'}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        );
      case 'user-management':
        return (
          <div className="sub-page-content">
            <div className="user-management-header">
              <h2>👥 사용자 관리</h2>
              <div className="user-management-controls">
                <input
                  type="text"
                  placeholder="사용자 이메일 또는 이름으로 검색"
                  value={userSearchTerm}
                  onChange={(e) => setUserSearchTerm(e.target.value)}
                />
                <button onClick={openCreateUserModal}>
                  사용자 생성
                </button>
                <button onClick={syncUsers} disabled={syncingUsers}>
                  {syncingUsers ? '동기화 중...' : '사용자 동기화'}
                </button>
              </div>
            </div>
            <div className="user-list-container">
              <table className="user-list">
                <thead>
                  <tr>
                    <th onClick={() => handleUserSort('email')}>
                      이메일 {userSortField === 'email' && (userSortDirection === 'asc' ? '↑' : '↓')}
                    </th>
                    <th onClick={() => handleUserSort('name')}>
                      이름 {userSortField === 'name' && (userSortDirection === 'asc' ? '↑' : '↓')}
                    </th>
                    <th onClick={() => handleUserSort('created_at')}>
                      생성일 {userSortField === 'created_at' && (userSortDirection === 'asc' ? '↑' : '↓')}
                    </th>
                    <th onClick={() => handleUserSort('updated_at')}>
                      업데이트일 {userSortField === 'updated_at' && (userSortDirection === 'asc' ? '↑' : '↓')}
                    </th>
                    <th>데이터 타입</th>
                    <th>비밀번호</th>
                    <th>관리자</th>
                    <th>작업</th>
                  </tr>
                </thead>
                <tbody>
                  {loadingUsers ? (
                    <tr>
                      <td colSpan="8" className="loading-row">사용자 목록을 불러오는 중입니다...</td>
                    </tr>
                  ) : getFilteredAndSortedUsers().length === 0 ? (
                    <tr>
                      <td colSpan="8" className="no-data-row">검색 결과가 없습니다.</td>
                    </tr>
                  ) : (
                    getFilteredAndSortedUsers().map((user, index) => (
                      <tr key={index}>
                        <td>{user.email}</td>
                        <td>{user.name}</td>
                        <td>{new Date(user.created_at).toLocaleDateString()}</td>
                        <td>{new Date(user.updated_at).toLocaleDateString()}</td>
                        <td>{user.available_data_types.join(', ')}</td>
                        <td>
                          <span className={`password-status ${user.has_password ? 'set' : 'not-set'}`}>
                            {user.has_password ? '✅ 설정됨' : '❌ 미설정'}
                          </span>
                        </td>
                        <td>
                          <span className={`admin-status ${user.is_admin ? 'admin' : 'user'}`}>
                            {user.is_admin ? '👑 관리자' : '👤 사용자'}
                          </span>
                        </td>
                        <td>
                          <button onClick={() => openUserEditModal(user)}>수정</button>
                          <button onClick={() => confirmAndToggleAdmin(user.email, user.is_admin)}>
                            {user.is_admin ? '관리자 해제' : '관리자 설정'}
                          </button>
                          <button onClick={() => deleteUser(user.email)}>삭제</button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* 사용자 수정 모달 */}
            {showUserEditModal && editingUser && (
              <div className="modal-overlay" onClick={closeUserEditModal}>
                <div className="modal-content user-edit-modal" onClick={e => e.stopPropagation()}>
                  <div className="modal-header">
                    <h2>사용자 정보 수정</h2>
                    <button className="modal-close" onClick={closeUserEditModal}>×</button>
                  </div>
                  <div className="modal-body">
                    <div className="user-edit-form">
                      <label>이메일:</label>
                      <input type="email" value={editingUser.email} onChange={(e) => setEditingUser({ ...editingUser, email: e.target.value })} disabled />
                      <label>이름:</label>
                      <input type="text" value={editingUser.name} onChange={(e) => setEditingUser({ ...editingUser, name: e.target.value })} />
                      <label>사용 가능한 데이터 타입:</label>
                      <div className="data-type-checkboxes">
                        <label>
                          <input
                            type="checkbox"
                            checked={editingUser.available_data_types.includes('legacy')}
                            onChange={(e) => handleCheckboxChange('legacy', e.target.checked)}
                          />
                          Legacy
                        </label>
                        <label>
                          <input
                            type="checkbox"
                            checked={editingUser.available_data_types.includes('preferred-return')}
                            onChange={(e) => handleCheckboxChange('preferred-return', e.target.checked)}
                          />
                          Preferred Return
                        </label>
                        <label>
                          <input
                            type="checkbox"
                            checked={editingUser.available_data_types.includes('fixed-income')}
                            onChange={(e) => handleCheckboxChange('fixed-income', e.target.checked)}
                          />
                          Fixed Income
                        </label>
                      </div>
                      <label>비밀번호 상태:</label>
                      <div className="password-status-display">
                        <span className={`password-status ${editingUser.has_password ? 'set' : 'not-set'}`}>
                          {editingUser.has_password ? '✅ 설정됨' : '❌ 미설정'}
                        </span>
                        {!editingUser.has_password && (
                          <small className="password-note">
                            사용자가 비밀번호 재설정을 통해 비밀번호를 설정할 수 있습니다.
                          </small>
                        )}
                      </div>
                      <button onClick={saveUserEdit}>저장</button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* 사용자 생성 모달 */}
            {showCreateUserModal && (
              <div className="modal-overlay" onClick={closeCreateUserModal}>
                <div className="modal-content user-edit-modal" onClick={e => e.stopPropagation()}>
                  <div className="modal-header">
                    <h2>새 사용자 생성</h2>
                    <button className="modal-close" onClick={closeCreateUserModal}>×</button>
                  </div>
                  <div className="modal-body">
                    <div className="user-edit-form">
                      <label>이메일:</label>
                      <input 
                        type="email" 
                        value={newUser.email} 
                        onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                        placeholder="user@example.com"
                      />
                      <label>이름:</label>
                      <input 
                        type="text" 
                        value={newUser.name} 
                        onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                        placeholder="사용자 이름"
                      />
                      <label>사용 가능한 데이터 타입:</label>
                      <div className="data-type-checkboxes">
                        <label>
                          <input
                            type="checkbox"
                            checked={newUser.available_data_types.includes('legacy')}
                            onChange={(e) => handleNewUserCheckboxChange('legacy', e.target.checked)}
                          />
                          Legacy
                        </label>
                        <label>
                          <input
                            type="checkbox"
                            checked={newUser.available_data_types.includes('preferred-return')}
                            onChange={(e) => handleNewUserCheckboxChange('preferred-return', e.target.checked)}
                          />
                          Preferred Return
                        </label>
                        <label>
                          <input
                            type="checkbox"
                            checked={newUser.available_data_types.includes('fixed-income')}
                            onChange={(e) => handleNewUserCheckboxChange('fixed-income', e.target.checked)}
                          />
                          Fixed Income
                        </label>
                      </div>
                      <small className="password-note">
                        사용자는 비밀번호 재설정을 통해 비밀번호를 설정할 수 있습니다.
                      </small>
                      <small className="external-data-note">
                        💡 외부 백엔드에 사용자 데이터가 없어도 사용자를 생성할 수 있습니다.
                      </small>
                      <button onClick={saveNewUser}>생성</button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      case 'asset-config':
        return (
          <div className="sub-page-content">
            <div className="asset-config-header">
              <h2>💰 자산 설정</h2>
              <div className="asset-config-controls">
                <button 
                  onClick={loadAssetConfigs} 
                  disabled={loadingAssetConfigs}
                  className="refresh-button"
                >
                  {loadingAssetConfigs ? '새로고침 중...' : '새로고침'}
                </button>
                <div className="asset-add-remove">
                  <input
                    type="text"
                    value={newAssetSymbol}
                    onChange={(e) => setNewAssetSymbol(e.target.value)}
                    placeholder="예: BTC, ETH, DOGE"
                    className="asset-symbol-input"
                    maxLength={12}
                  />
                  <button
                    onClick={() => handleAddAsset()}
                    disabled={updatingAssetOrder}
                    className="add-asset-button"
                  >
                    자산 추가
                  </button>
                  <button
                    onClick={() => handleRemoveAsset()}
                    disabled={updatingAssetOrder}
                    className="remove-asset-button"
                  >
                    자산 제거
                  </button>
                </div>
              </div>
            </div>
            
            <div className="asset-config-container">
              {loadingAssetConfigs ? (
                <div className="loading-container">
                  <div className="loading-spinner"></div>
                  <p>자산 설정을 불러오는 중입니다...</p>
                </div>
              ) : (
                <div className="asset-list">
                  <div className="asset-list-header">
                    <h3>자산 목록 및 순서</h3>
                    <p>드래그하여 자산 순서를 변경할 수 있습니다.</p>
                  </div>
                  
                  <div className="asset-items">
                    {assetConfigs.map((asset, index) => (
                      <div 
                        key={asset.id} 
                        className={`asset-item ${!asset.is_active ? 'inactive' : ''}`}
                        draggable
                        onDragStart={(e) => {
                          e.dataTransfer.setData('text/plain', index.toString());
                        }}
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={(e) => {
                          e.preventDefault();
                          const draggedIndex = parseInt(e.dataTransfer.getData('text/plain'));
                          if (draggedIndex !== index) {
                            const newOrder = [...assetConfigs];
                            const draggedItem = newOrder[draggedIndex];
                            newOrder.splice(draggedIndex, 1);
                            newOrder.splice(index, 0, draggedItem);
                            updateAssetOrder(newOrder.map(item => item.symbol));
                          }
                        }}
                      >
                        <div className="asset-symbol">
                          <span className="asset-icon">💰</span>
                          <span className="symbol-text">{asset.symbol}</span>
                        </div>
                        <div className="asset-status">
                          <span className={`status-badge ${asset.is_active ? 'active' : 'inactive'}`}>
                            {asset.is_active ? '활성' : '비활성'}
                          </span>
                        </div>
                        <div className="asset-order">
                          <span className="order-number">{index + 1}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  <div className="asset-info">
                    <h4>자산 정보</h4>
                    <div className="info-grid">
                      {assetConfigs.map((asset, index) => (
                        <div key={asset.id} className="info-item">
                          <div className="info-header">
                            <span className="symbol">{asset.symbol}</span>
                            <span className="order">#{index + 1}</span>
                          </div>
                          <div className="info-details">
                            <div className="detail-row">
                              <span className="label">상태:</span>
                              <span className={`value ${asset.is_active ? 'active' : 'inactive'}`}>
                                {asset.is_active ? '활성' : '비활성'}
                              </span>
                            </div>
                            <div className="detail-row">
                              <span className="label">ID:</span>
                              <span className="value">{asset.id}</span>
                            </div>
                            <div className="detail-row">
                              <span className="label">생성일:</span>
                              <span className="value">
                                {new Date(asset.created_at).toLocaleDateString()}
                              </span>
                            </div>
                            <div className="detail-row">
                              <span className="label">수정일:</span>
                              <span className="value">
                                {new Date(asset.updated_at).toLocaleDateString()}
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      case 'security-monitoring':
        return (
          <div className="sub-page-content">
            <div className="security-monitoring-header">
              <h2>🔒 보안 모니터링</h2>
              <div className="security-controls">
                <button onClick={loadSecurityData} disabled={loadingSecurity}>
                  {loadingSecurity ? '새로고침 중...' : '새로고침'}
                </button>
                <button onClick={() => {
                  const interval = setInterval(loadSecurityData, 30000); // 30초마다 자동 새로고침
                  setSecurityRefreshInterval(interval);
                  showNotification('자동 새로고침이 활성화되었습니다.', 'info');
                }} disabled={securityRefreshInterval}>
                  자동 새로고침 시작
                </button>
                <button onClick={() => {
                  if (securityRefreshInterval) {
                    clearInterval(securityRefreshInterval);
                    setSecurityRefreshInterval(null);
                    showNotification('자동 새로고침이 중지되었습니다.', 'info');
                  }
                }} disabled={!securityRefreshInterval}>
                  자동 새로고침 중지
                </button>
              </div>
            </div>

            {loadingSecurity ? (
              <div className="loading-container">
                <div className="loading-spinner"></div>
                <p>보안 데이터를 불러오는 중입니다...</p>
              </div>
            ) : securityData ? (
              <div className="security-dashboard">
                {/* 요약 통계 */}
                <div className="security-summary">
                  <div className="summary-card">
                    <h3>차단된 계정</h3>
                    <span className="summary-value">{securityData.totalBlockedAccounts}</span>
                  </div>
                  <div className="summary-card">
                    <h3>최근 시도</h3>
                    <span className="summary-value">{securityData.totalRecentAttempts}</span>
                  </div>
                </div>

                {/* 차단된 계정 목록 */}
                {securityData.blockedAccounts.length > 0 && (
                  <div className="security-section">
                    <h3>🚫 차단된 계정</h3>
                    <div className="blocked-accounts-list">
                      {securityData.blockedAccounts.map((account, index) => (
                        <div key={index} className="blocked-item">
                          <div className="blocked-info">
                            <div className="blocked-email">{account.email}</div>
                            <div className="blocked-reason">{account.reason}</div>
                            <div className="blocked-time">
                              차단 해제: {formatTime(account.blockedUntil)} 
                              ({formatRemainingTime(account.blockedUntil)} 남음)
                            </div>
                          </div>
                          <button 
                            className="unblock-button"
                            onClick={() => unblockAccount(account.email)}
                          >
                            차단 해제
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}


                {/* 최근 로그인 시도 */}
                {securityData.recentAttempts.length > 0 && (
                  <div className="security-section">
                    <h3>📊 최근 로그인 시도</h3>
                    <div className="attempts-table">
                      <table>
                        <thead>
                          <tr>
                            <th>이메일</th>
                            <th>총 시도</th>
                            <th>실패</th>
                            <th>성공</th>
                            <th>백오프 레벨</th>
                            <th>마지막 시도</th>
                          </tr>
                        </thead>
                        <tbody>
                          {securityData.recentAttempts.map((attempt, index) => (
                            <tr key={index}>
                              <td>{attempt.email}</td>
                              <td>{attempt.totalAttempts}</td>
                              <td className={attempt.failedAttempts > 0 ? 'failed' : ''}>{attempt.failedAttempts}</td>
                              <td className={attempt.successAttempts > 0 ? 'success' : ''}>{attempt.successAttempts}</td>
                              <td>{attempt.backoffLevel}</td>
                              <td>{formatTime(attempt.lastAttempt)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}


                {/* 보안 설정 정보 */}
                <div className="security-section">
                  <h3>⚙️ 보안 설정</h3>
                  <div className="security-config">
                    <div className="config-item">
                      <span className="config-label">계정별 최대 시도:</span>
                      <span className="config-value">{securityData.securityConfig.MAX_ACCOUNT_ATTEMPTS}회 / {securityData.securityConfig.ACCOUNT_WINDOW / 60000}분</span>
                    </div>
                    <div className="config-item">
                      <span className="config-label">계정 차단 시간:</span>
                      <span className="config-value">{securityData.securityConfig.ACCOUNT_BLOCK_DURATION / 60000}분</span>
                    </div>
                    <div className="config-item">
                      <span className="config-label">CAPTCHA 요구 시도:</span>
                      <span className="config-value">{securityData.securityConfig.CAPTCHA_REQUIRED_ATTEMPTS}회</span>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="no-data">
                <p>보안 데이터를 불러오지 못했습니다.</p>
                <button onClick={loadSecurityData}>다시 시도</button>
              </div>
            )}
          </div>
        );
      case 'system-settings':
        return (
          <div className="sub-page-content">
            <div className="coming-soon">
              <h2>⚙️ 시스템 설정</h2>
              <p>시스템 설정 기능이 곧 추가될 예정입니다.</p>
            </div>
          </div>
        );
      case 'analytics':
        return (
          <div className="sub-page-content">
            <div className="coming-soon">
              <h2>📈 분석 대시보드</h2>
              <p>분석 대시보드 기능이 곧 추가될 예정입니다.</p>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  useEffect(() => {
    loadCsvData();
    
    const dataTypeFromUrl = searchParams.get('dataType');
    if (dataTypeFromUrl) {
      setDataType(dataTypeFromUrl);
    }
  }, [loadCsvData, searchParams]);

  // 사용자 관리 페이지로 이동할 때 사용자 목록 로드
  useEffect(() => {
    if (currentSubPage === 'user-management') {
      loadUsers();
    }
  }, [currentSubPage]);

  useEffect(() => {
    if (currentSubPage === 'legacy-flow-history') {
      loadUsers();
    }
  }, [currentSubPage]);

  useEffect(() => {
    if (currentSubPage === 'fixed-income-deposit-history') {
      loadUsers();
    }
  }, [currentSubPage]);

  useEffect(() => {
    if (currentSubPage === 've-token-prices') {
      loadVeTokenPrices();
    }
  }, [currentSubPage]);

  useEffect(() => {
    if (currentSubPage !== 'legacy-flow-history') return;
    const matched = legacyFlowUsers.some((u) => u.email === legacyFlowEmail);
    if (matched) return;
    if (legacyFlowUsers.length > 0) {
      setLegacyFlowEmail(legacyFlowUsers[0].email);
    } else if (legacyFlowEmail) {
      setLegacyFlowEmail('');
    }
  }, [currentSubPage, legacyFlowUsers, legacyFlowEmail]);

  useEffect(() => {
    if (currentSubPage !== 'legacy-flow-history') return;
    if (!legacyFlowEmail) return;
    loadLegacyFlowHistory(legacyFlowEmail, legacyFlowLimit);
  }, [currentSubPage, legacyFlowEmail, legacyFlowLimit]);

  useEffect(() => {
    if (currentSubPage !== 'fixed-income-deposit-history') return;
    const matched = fixedIncomeFlowUsers.some((u) => u.email === fixedIncomeFlowEmail);
    if (matched) return;
    if (fixedIncomeFlowUsers.length > 0) {
      setFixedIncomeFlowEmail(fixedIncomeFlowUsers[0].email);
    } else if (fixedIncomeFlowEmail) {
      setFixedIncomeFlowEmail('');
    }
  }, [currentSubPage, fixedIncomeFlowUsers, fixedIncomeFlowEmail]);

  useEffect(() => {
    if (currentSubPage !== 'fixed-income-deposit-history') return;
    if (!fixedIncomeFlowEmail) return;
    loadFixedIncomeDepositHistory(fixedIncomeFlowEmail, fixedIncomeFlowLimit);
  }, [currentSubPage, fixedIncomeFlowEmail, fixedIncomeFlowLimit]);

  // 보안 모니터링 페이지가 활성화될 때 데이터 로드
  useEffect(() => {
    if (currentSubPage === 'security-monitoring') {
      loadSecurityData();
    }
  }, [currentSubPage]);

  // 컴포넌트 언마운트 시 타이머 정리
  useEffect(() => {
    return () => {
      if (securityRefreshInterval) {
        clearInterval(securityRefreshInterval);
      }
    };
  }, [securityRefreshInterval]);

  // 자산 설정 페이지가 활성화될 때 데이터 로드
  useEffect(() => {
    if (currentSubPage === 'asset-config') {
      loadAssetConfigs();
    }
  }, [currentSubPage]);

  // 자산 설정 로드 함수
  const loadAssetConfigs = async () => {
    try {
      setLoadingAssetConfigs(true);
      const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
      const response = await axios.get('/api/asset-configs', {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setAssetConfigs(response.data.data);
      } else {
        showNotification('자산 설정을 불러오는데 실패했습니다.', 'error');
      }
    } catch (error) {
      console.error('Error loading asset configs:', error);
      showNotification('자산 설정을 불러오는데 실패했습니다.', 'error');
    } finally {
      setLoadingAssetConfigs(false);
    }
  };

  // 자산 순서 업데이트 함수
  const updateAssetOrder = async (newOrder) => {
    try {
      setUpdatingAssetOrder(true);
      const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
      const response = await axios.patch('/api/asset-configs/order', {
        asset_symbols: newOrder
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setAssetConfigs(response.data.data);
        showNotification('자산 순서가 성공적으로 업데이트되었습니다.', 'success');
      } else {
        showNotification('자산 순서 업데이트에 실패했습니다.', 'error');
      }
    } catch (error) {
      console.error('Error updating asset order:', error);
      showNotification('자산 순서 업데이트에 실패했습니다.', 'error');
    } finally {
      setUpdatingAssetOrder(false);
    }
  };

  // 자산 추가/제거 핸들러 (순서 PATCH 사용)
  const normalizeSymbol = (s) => (s || '').toString().trim().toUpperCase();
  const handleAddAsset = () => {
    const sym = normalizeSymbol(newAssetSymbol);
    if (!sym) {
      showNotification('자산 심볼을 입력해주세요. (예: BTC, ETH, DOGE)', 'error');
      return;
    }
    // 간단 검증: 영문/숫자/하이픈/언더스코어 허용, 2~12자
    if (!/^[A-Z0-9._-]{2,12}$/.test(sym)) {
      showNotification('유효한 심볼 형식을 입력해주세요. (영문/숫자/.-_/ 2~12자)', 'error');
      return;
    }
    const currentSymbols = assetConfigs.map(a => a.symbol);
    if (currentSymbols.includes(sym)) {
      showNotification(`${sym} 은(는) 이미 목록에 있습니다.`, 'info');
      return;
    }
    const newOrder = [...currentSymbols, sym];
    updateAssetOrder(newOrder).then(() => {
      setNewAssetSymbol('');
    });
  };

  const handleRemoveAsset = () => {
    const sym = normalizeSymbol(newAssetSymbol);
    if (!sym) {
      showNotification('제거할 자산 심볼을 입력해주세요.', 'error');
      return;
    }
    const currentSymbols = assetConfigs.map(a => a.symbol);
    if (!currentSymbols.includes(sym)) {
      showNotification(`${sym} 은(는) 현재 목록에 없습니다.`, 'warning');
      return;
    }
    const newOrder = currentSymbols.filter(s => s !== sym);
    updateAssetOrder(newOrder).then(() => {
      setNewAssetSymbol('');
    });
  };

  const toggleAdminStatus = async (email) => {
    try {
      const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
      const response = await axios.post(`/api/admin/users/${encodeURIComponent(email)}/toggle-admin`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        const newStatus = response.data.user.is_admin ? '관리자로 설정' : '일반 사용자로 설정';
        showNotification(`사용자가 ${newStatus}되었습니다.`, 'success');
        await loadUsers(); // 사용자 목록 새로고침
      } else {
        showNotification('관리자 권한 변경에 실패했습니다.', 'error');
      }
    } catch (error) {
      console.error('Error toggling admin status:', error);
      if (error.response?.data?.message) {
        showNotification(`관리자 권한 변경 실패: ${error.response.data.message}`, 'error');
      } else {
        showNotification('관리자 권한 변경에 실패했습니다.', 'error');
      }
    }
  };

  const confirmAndToggleAdmin = async (email, isAdmin) => {
    const message = isAdmin
      ? `정말로 ${email}의 관리자 권한을 해제하시겠습니까?\n(관리자 권한이 없으면 관리자 페이지 접근이 제한됩니다.)`
      : `정말로 ${email}을(를) 관리자 권한으로 설정하시겠습니까?\n(관리자는 모든 사용자와 데이터를 관리할 수 있습니다.)`;
    if (!window.confirm(message)) return;
    await toggleAdminStatus(email);
  };

  const createUser = async (userData) => {
    try {
      const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
      const response = await axios.post('/api/admin/users', userData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        showNotification('사용자가 성공적으로 생성되었습니다.', 'success');
        await loadUsers(); // 사용자 목록 새로고침
        return true;
      } else {
        showNotification('사용자 생성에 실패했습니다.', 'error');
        return false;
      }
    } catch (error) {
      console.error('Error creating user:', error);
      if (error.response?.data?.message) {
        showNotification(`사용자 생성 실패: ${error.response.data.message}`, 'error');
      } else {
        showNotification('사용자 생성에 실패했습니다.', 'error');
      }
      return false;
    }
  };

  const openCreateUserModal = () => {
    setNewUser({
      email: '',
      name: '',
      available_data_types: []
    });
    setShowCreateUserModal(true);
  };

  const closeCreateUserModal = () => {
    setNewUser({
      email: '',
      name: '',
      available_data_types: []
    });
    setShowCreateUserModal(false);
  };

  const handleNewUserCheckboxChange = (dataType, checked) => {
    let newAvailableTypes;
    
    if (checked) {
      // 체크박스가 체크된 경우 - 데이터 타입 추가
      newAvailableTypes = [...newUser.available_data_types, dataType];
    } else {
      // 체크박스가 해제된 경우 - 데이터 타입 제거
      newAvailableTypes = newUser.available_data_types.filter(type => type !== dataType);
    }
    
    setNewUser({ ...newUser, available_data_types: newAvailableTypes });
  };

  const saveNewUser = async () => {
    if (!newUser.email || !newUser.name) {
      showNotification('이메일과 이름은 필수 입력 항목입니다.', 'error');
      return;
    }
    
    const success = await createUser({
      email: newUser.email,
      name: newUser.name,
      available_data_types: newUser.available_data_types
    });
    
    if (success) {
      closeCreateUserModal();
    }
  };

  // 보안 모니터링 함수들
  const loadSecurityData = async () => {
    setLoadingSecurity(true);
    try {
      const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
      const response = await axios.get('/api/admin/security/status', {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setSecurityData(response.data.data);
      } else {
        showNotification('보안 데이터를 불러오는데 실패했습니다.', 'error');
      }
    } catch (error) {
      console.error('Error loading security data:', error);
      showNotification('보안 데이터를 불러오는데 실패했습니다.', 'error');
    } finally {
      setLoadingSecurity(false);
    }
  };

  const unblockAccount = async (email) => {
    try {
      const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
      const response = await axios.post('/api/admin/security/unblock-account', {
        email
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        showNotification('계정 차단이 해제되었습니다.', 'success');
        await loadSecurityData(); // 데이터 새로고침
      } else {
        showNotification('계정 차단 해제에 실패했습니다.', 'error');
      }
    } catch (error) {
      console.error('Error unblocking account:', error);
      showNotification('계정 차단 해제에 실패했습니다.', 'error');
    }
  };


  // 시간 포맷팅 함수
  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleString();
  };

  const formatRemainingTime = (timestamp) => {
    const now = Date.now();
    const remaining = Math.max(0, timestamp - now);
    const minutes = Math.floor(remaining / 60000);
    const seconds = Math.floor((remaining % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  return (
    <div className="admin-page">
      <div className="admin-container">
        {/* Admin Navigation Bar */}
        <div className="admin-navbar">
          <div className="nav-left">
            <div className="nav-link">
              <span className="nav-icon">🔧</span>
              <span className="nav-title">관리자 도구</span>
            </div>
          </div>
          <div className="nav-right">
            <button className="nav-button" onClick={onLogout}>
              로그아웃
            </button>
          </div>
        </div>

        {/* Sub Navigation */}
        <div className="sub-navbar">
          {subPages.map(page => (
            <button
              key={page.id}
              className={`sub-nav-button ${currentSubPage === page.id ? 'active' : ''}`}
              onClick={() => setCurrentSubPage(page.id)}
            >
              <span className="sub-nav-icon">{page.icon}</span>
              {page.title}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="admin-content">
          {renderSubPage()}
        </div>

        {/* History UI 제거됨 */}

        {/* Notification */}
        {notification && (
          <div className={`notification ${notification.type}`}>
            {notification.message}
          </div>
        )}

        {/* Custom Tooltip */}
        {tooltip.show && (
          <div 
            className="custom-tooltip"
            style={{
              position: 'fixed',
              left: tooltip.x,
              top: tooltip.y,
              zIndex: 10000,
              transform: 'translate(-50%, -100%)'
            }}
          >
            {tooltip.text}
          </div>
        )}

        {/* Sibling Selection Popup */}
        {showSiblingPopup && siblingPopupNode && (
          <div className="modal-overlay" onClick={() => setShowSiblingPopup(false)}>
            <div className="modal-content sibling-popup" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h2>🔄 형제 노드 선택</h2>
                <button className="modal-close" onClick={() => setShowSiblingPopup(false)}>×</button>
              </div>
              <div className="modal-body">
                <div className="sibling-popup-content">
                  <p className="sibling-popup-description">
                    현재 노드: <strong>{siblingPopupNode.hash}</strong>
                  </p>
                  <div className="sibling-list">
                    {siblingPopupNode.siblings.map((sibling, index) => (
                      <div 
                        key={sibling.hash} 
                        className="sibling-option"
                        onClick={() => handleSiblingPopupSelect(sibling)}
                      >
                        <div className="sibling-hash">{sibling.hash}</div>
                        <div className="sibling-timestamp">
                          {new Date(sibling.timestamp).toLocaleString()}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPage; 
